import React, { useEffect, useState, useCallback } from 'react';
import { LuBanknote, LuCircleCheck, LuArchive, LuLayers, LuShoppingCart, LuDownload, LuListOrdered, LuFilePen } from 'react-icons/lu';
import ModalCotizacionVolumen from '../presupuesto/ModalCotizacionVolumen';
import ModalPropuestaAlquiler from '../presupuesto/ModalPropuestaAlquiler';
import BusquedaBar from '../ui/BusquedaBar';
import ChipFiltro from '../ui/ChipFiltro';
import { periodoLabelDe, getTodayISO } from '../../utils/dateUtils';
import { useVentaManager } from '../../hooks/useVentaManager';
import { useAuth } from '../../context/AuthContext';
import VentaList   from './VentaList';
import VentaForm   from './VentaForm';
import Paginacion   from '../ui/Paginacion';
import { CONTENEDOR, PantallaHeader, BotonPrimario, BotonHerramienta, Herramientas, Pestanas, PAGINA, BotonFlotante } from '../ui/Pantalla';
import FiltrosPanel from '../ui/FiltrosPanel';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import { exportarVentasCSV } from '../../utils/exportarCSV';
import api from '../../services/api';

const TABS = [
    { id: 'PRESUPUESTO', label: 'En curso',   short: 'En curso', color: 'var(--etapa-cobrar)', Icon: LuBanknote },
    { id: 'REALIZADO',   label: 'Cobradas',   short: 'Cobradas', color: 'var(--etapa-listo)', Icon: LuCircleCheck },
    { id: 'ARCHIVADO',   label: 'Archivadas', short: 'Arch',   color: '#A8A29E', Icon: LuArchive },
    // "Todo" — mismo patrón que ya tiene Servicio Técnico: búsqueda libre sin
    // filtro de estado, con rango de fechas. Jubila a "Historial" como pantalla
    // aparte (ver ServicioList.jsx, ahora sin uso — 26-ago).
    { id: 'TODOS',        label: 'Todo',       short: 'Todo',    color: null, Icon: LuLayers },
];

const ESTADO_API_MAP = {
    REALIZADO: 'REALIZADO',
};

export default function VentaManager({ clienteInicial = null, onClienteConsumido, abrirCrearDirecto = false, onCrearConsumido }) {
    const { esAdmin } = useAuth();
    const [listaPrecios, setListaPrecios] = useState(false);
    const [alquiler, setAlquiler] = useState(false);
    const {
        cargando,
        modalCrear, setModalCrear,
        ventaEditar,
        cargarVentas,
        confirmarVenta,
        eliminarVenta,
        generarPDF,
        calcularTotal,
        abrirEditar,
        cerrarModal,
        filtros,
    } = useVentaManager();

    const [ventaDuplicar, setVentaDuplicar] = useState(null);
    const [tabCounts, setTabCounts] = useState({});
    const [mostrarFiltros, setMostrarFiltros] = useState(false);
    const [tabAntesBusqueda, setTabAntesBusqueda] = useState(null);

    const tabActual = filtros.estado || 'PRESUPUESTO';

    // Auto-switch a "Todo" cuando se escribe en el buscador (mismo patrón que
    // ServicioManager.jsx): si no, la búsqueda queda acotada a la pestaña activa
    // y no encuentra ventas que estén en otro estado. Vuelve a la pestaña
    // anterior al borrar la búsqueda.
    useEffect(() => {
        if (filtros.busqueda && tabActual !== 'TODOS') {
            setTabAntesBusqueda(tabActual);
            filtros.setEstado('TODOS');
        } else if (!filtros.busqueda && tabActual === 'TODOS' && tabAntesBusqueda) {
            filtros.setEstado(tabAntesBusqueda);
            setTabAntesBusqueda(null);
        }
    }, [filtros.busqueda]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cargar conteos por estado
    const fetchTabCounts = useCallback(async () => {
        try {
            const results = await Promise.all(
                TABS.map(t => api.get('/servicios', {
                    params: {
                        ...filtros.paramsBase,
                        ...(t.id !== 'TODOS' ? { estado: ESTADO_API_MAP[t.id] || t.id } : {}),
                        page: 0, size: 1,
                    }
                }).catch(() => ({ data: { totalElements: 0 } })))
            );
            const counts = {};
            TABS.forEach((t, i) => { counts[t.id] = results[i].data.totalElements || 0; });
            setTabCounts(counts);
        } catch (err) { console.warn('Ventas: error cargando conteos tabs', err); }
    }, [filtros.paramsBase]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchTabCounts(); }, [fetchTabCounts]);
    useEffect(() => {
        setTabCounts(prev => ({ ...prev, [tabActual]: filtros.totalItems }));
    }, [filtros.totalItems, tabActual]);

    const cambiarTab = (id) => {
        filtros.setEstado(id);
    };

    useEffect(() => { if (clienteInicial) setModalCrear(true); }, [clienteInicial, setModalCrear]);
    useEffect(() => { if (abrirCrearDirecto) { setModalCrear(true); onCrearConsumido?.(); } }, [abrirCrearDirecto]); // eslint-disable-line

    const duplicarVenta = (v) => {
        setVentaDuplicar({
            ...v, id: undefined, estado: 'PRESUPUESTO', nroDocumento: undefined,
            fecha: getTodayISO(),
        });
        setModalCrear(true);
    };
    const cerrarModalDuplicar = () => { cerrarModal(); setVentaDuplicar(null); };

    const confirmarConRefresh = async (...args) => {
        await confirmarVenta(...args);
        fetchTabCounts();
    };

    // Bug real (9-sep): eliminarVenta borraba la venta pero nunca refrescaba
    // tabCounts -- a diferencia de confirmar/crear, que sí lo hacen -- así que
    // "Cobradas"/"Archivadas"/"Todo" quedaban con el número viejo hasta recargar
    // la página entera. Mismo criterio que confirmarConRefresh.
    const eliminarConRefresh = async (...args) => {
        await eliminarVenta(...args);
        fetchTabCounts();
    };

    const columns = TABS.map(t => ({
        id: t.id, label: t.short, fullLabel: t.label,
        count: tabCounts[t.id] ?? null, color: t.color, Icon: t.Icon,
    }));

    const columnIds = TABS.map(t => t.id);
    const swipeHandlers = useSwipeGesture(columnIds, tabActual, cambiarTab);

    return (
        <div className={PAGINA}
            {...swipeHandlers}>

            <div className={CONTENEDOR}>
                <PantallaHeader titulo="Ventas" subtitulo="Ventas de productos, de presupuesto a cobrada"
                    busqueda={<BusquedaBar valor={filtros.busqueda} onChange={filtros.setBusqueda} placeholder="Cliente, producto, sede…" />}
                    accion={esAdmin && <BotonPrimario onClick={() => setModalCrear(true)}>Nueva venta</BotonPrimario>} />

                {/* SwipeColumns */}
                <Pestanas items={TABS.map(t => ({ id: t.id, label: t.label, count: columns.find(c => c.id === t.id)?.count, color: t.color }))}
                    activo={tabActual} onChange={cambiarTab} />

                <Herramientas>
                    <BotonHerramienta icono={LuDownload} onClick={() => exportarVentasCSV(filtros.itemsFiltrados)}>Exportar</BotonHerramienta>
                    {esAdmin && <BotonHerramienta icono={LuListOrdered} onClick={() => setListaPrecios(true)}>Precios x cantidad</BotonHerramienta>}
                    {/* 31-oct-2026: propuesta de alquiler a empresas y acta de entrega (Presupuestos ya no está en el menú) */}
                    {esAdmin && <BotonHerramienta icono={LuFilePen} onClick={() => setAlquiler(true)}>Alquiler</BotonHerramienta>}
                </Herramientas>

                {/* Filtros colapsables — solo en "Todo": las otras 3 pestañas ya filtran
                    por estado, acá es donde tiene sentido acotar por fecha */}
                {tabActual === 'TODOS' && (
                    <>
                        <div className="flex items-center justify-between">
                            <ChipFiltro label={periodoLabelDe(filtros)} activo={mostrarFiltros} onClick={() => setMostrarFiltros(v => !v)} />
                            <span className="text-label font-bold text-muted">{filtros.totalItems} resultados</span>
                        </div>
                        {mostrarFiltros && (
                            <FiltrosPanel hook={filtros} conBusqueda={false} conRango />
                        )}
                    </>
                )}

                {/* Lista */}
                {cargando ? (
                    <div className="flex flex-col gap-2">
                        {[1, 2, 3].map(i => <div key={i} className="h-28 rounded-2xl animate-pulse bg-card" />)}
                    </div>
                ) : filtros.itemsPagina.length === 0 ? (
                    <div className="text-center py-16 rounded-2xl bg-card border border-black/[0.07] dark:border-white/[0.07]">
                        {(() => { const EmptyIcon = TABS.find(t => t.id === tabActual)?.Icon || LuShoppingCart; return <EmptyIcon size={32} className="mb-2 text-muted inline-block" />; })()}
                        <p className="text-body font-bold text-muted">{tabActual === 'TODOS' ? 'No hay ventas en este período' : `No hay ventas en "${TABS.find(t => t.id === tabActual)?.label}"`}</p>
                        {esAdmin && (
                            <button onClick={() => setModalCrear(true)}
                                className="mt-4 h-11 px-5 rounded-xl bg-[#C9341F] text-white text-label font-black active:scale-95">+ Cargar una venta</button>
                        )}
                    </div>
                ) : (
                    <VentaList
                        ventas={filtros.itemsPagina}
                        cargando={false}
                        busqueda={filtros.busqueda} setBusqueda={filtros.setBusqueda}
                        filtroTab="TODOS" setFiltroTab={() => {}}
                        calcularTotal={calcularTotal}
                        onEditar={abrirEditar}
                        onConfirmar={confirmarConRefresh}
                        onEliminar={eliminarConRefresh}
                        onPDF={generarPDF}
                        onDuplicar={duplicarVenta}
                    />
                )}

                <Paginacion pagina={filtros.pagina} totalPaginas={filtros.totalPaginas} irA={filtros.irA} next={filtros.next} prev={filtros.prev} />
            </div>

            {/* FAB — mobile. Mismo tratamiento que "Nueva Venta" en el Panel y en el
                header de esta pantalla: secundario, no ambar solido — el rojo de marca
                queda como unico acento fuerte de la app (ver "Nuevo Servicio"). */}
            {esAdmin && (
                <BotonFlotante onClick={() => setModalCrear(true)} label="Nueva venta" />
            )}

            {/* Modal crear/editar */}
            {listaPrecios && <ModalCotizacionVolumen onCerrar={() => setListaPrecios(false)} />}
            {alquiler && <ModalPropuestaAlquiler onCerrar={() => setAlquiler(false)} />}

            {modalCrear && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[2000] flex items-end md:items-center justify-center p-0 md:p-4 md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6">
                    <div className="bg-card w-full md:max-w-2xl md:rounded-3xl max-h-[calc(var(--vh,1vh)*95)] overflow-y-auto shadow-2xl">
                        <div className="md:hidden flex justify-center pt-3 pb-1">
                            <div className="w-10 h-1 rounded-full bg-[#E8E5E0] dark:bg-[#3E3E3E]" />
                        </div>
                        <div className="sticky top-0 bg-panel px-5 py-4 border-b border-black/[0.08] dark:border-white/[0.07] flex justify-between items-center z-10 md:rounded-t-3xl">
                            <div>
                                <h3 className="text-title font-black text-ink">
                                    {ventaDuplicar ? 'Duplicar Venta' : ventaEditar ? 'Editar Venta' : 'Nueva Venta'}
                                </h3>
                                <p className="text-caption text-muted mt-0.5">
                                    {ventaDuplicar ? 'Copia — ajustá y guardá'
                                        : ventaEditar ? `#${ventaEditar.id}`
                                        : 'Seleccioná cliente y productos'}
                                </p>
                            </div>
                            <button onClick={cerrarModalDuplicar}
                                className="w-9 h-9 rounded-xl flex items-center justify-center text-muted bg-chip active:scale-90">✕</button>
                        </div>
                        <VentaForm
                            onSaved={() => { cerrarModalDuplicar(); cargarVentas(); fetchTabCounts(); if (onClienteConsumido) onClienteConsumido(); }}
                            ventaParaEditar={ventaEditar || ventaDuplicar}
                            clienteInicialId={clienteInicial?.id}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
